import { isValidBin, isValidInn } from '@platform/components/ext_function';
import UserStore from '@platform/store/user';

export type PartyCountry = 'RU' | 'KZ';

export type PartySuggestion = {
    value: string;
    unrestricted_value: string;
    sourceCountry: PartyCountry;
    data: any;
};

export type PartyLookupPlan =
    | { kind: 'none' }
    | { kind: 'suggest'; country: PartyCountry }
    | { kind: 'find'; countries: PartyCountry[] };

/** Пустая страна считается Россией. Узбекистан и прочие справочник контрагентов не вызывают. */
export function primaryPartyCountry(country?: string | null): PartyCountry | null {
    if (!country || country === 'RU') return 'RU';
    if (country === 'KZ') return 'KZ';
    return null;
}

export function settingsPartyCountry(mode: 'settings' | 'default-ru' = 'settings'): string {
    if (mode === 'default-ru') return 'RU';
    return UserStore.userState.sysOptions?.platform$contragent?.COUNTRY || 'RU';
}

/**
 * Название — только suggest основной страны.
 * Номер — findById стран, чью контрольную сумму он проходит, основная первая.
 * Синхронно с platform-server-node/libs/doc-recognition/dadata/dadata.party.ts.
 */
export function planPartyLookup(country: string | null | undefined, query: string): PartyLookupPlan {
    const primary = primaryPartyCountry(country);
    const q = query?.trim() ?? '';
    if (!primary || !q) return { kind: 'none' };
    if (!/^\d+$/.test(q)) return { kind: 'suggest', country: primary };

    const secondary: PartyCountry = primary === 'RU' ? 'KZ' : 'RU';
    const countries: PartyCountry[] = [];
    if (checksumMatches(primary, q)) countries.push(primary);
    if (checksumMatches(secondary, q)) countries.push(secondary);
    if (!countries.length) return { kind: 'none' };
    return { kind: 'find', countries };
}

function checksumMatches(country: PartyCountry, value: string): boolean {
    return country === 'RU' ? isValidInn(value) : isValidBin(value);
}

export function normalizeRuParty(suggestion: {
    value: string;
    unrestricted_value?: string;
    data: any;
}): PartySuggestion {
    return {
        value: suggestion.value,
        unrestricted_value: suggestion.unrestricted_value ?? suggestion.value,
        sourceCountry: 'RU',
        data: suggestion.data,
    };
}

/** Казахский ответ приводится к полям российского, чтобы действующие мапперы карточки не ветвились. */
export function normalizeKzParty(suggestion: {
    value: string;
    unrestricted_value?: string;
    data: any;
}): PartySuggestion {
    const data = suggestion.data ?? {};
    const name = firstNonEmpty(data.name_ru, data.name_kz);
    const address = firstNonEmpty(data.address_ru, data.address_kz);
    return {
        value: name ?? suggestion.value,
        unrestricted_value: name ?? suggestion.unrestricted_value ?? suggestion.value,
        sourceCountry: 'KZ',
        data: {
            inn: data.bin ?? null,
            kpp: null,
            ogrn: null,
            ogrn_date: registrationTimestamp(data.registration_date),
            okpo: null,
            oktmo: null,
            address: address ? { unrestricted_value: address, value: address } : null,
            management: data.fio ? { name: data.fio, post: null } : null,
            name: name
                ? { short_with_opf: name, short: name, full_with_opf: name, full: name }
                : null,
        },
    };
}

/** Плоский вид, который уже ждёт fieldMapping грида контрагента. */
export function partySuggestionToGridOption(suggestion: PartySuggestion) {
    const data = suggestion.data ?? {};
    const displayName = data.name?.short_with_opf || suggestion.value;
    return {
        label: displayName,
        name: displayName,
        full_name: data.name?.full_with_opf || displayName,
        inn: data.inn ?? null,
        kpp: data.kpp ?? null,
        ogrn: data.ogrn ?? null,
        chief_post: data.management?.post ?? null,
        chief_fio: data.management?.name ?? null,
        sourceCountry: suggestion.sourceCountry,
    };
}

/** DaData отдаёт registration_date строкой из миллисекунд. Поле даты карточки понимает только число. */
function registrationTimestamp(value: unknown): number | string | null {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && /^\d{12,}$/.test(value.trim())) return Number(value);
    return value as string;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
    for (const value of values) {
        const trimmed = typeof value === 'string' ? value.trim() : '';
        if (trimmed) return trimmed;
    }
    return null;
}
